import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getAuth,
  multiFactor,
  MultiFactorInfo,
  MultiFactorUser,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
  reload,
  signOut
} from 'firebase/auth';
import QRCode from 'qrcode.react';
// @ts-ignore
import CompanyLogo from '../../assets/company-logo.png';
import { useNavigate, useLocation } from 'react-router-dom';
// @ts-ignore
import portalSettings from '../../version.json';
import { useSelector } from 'react-redux';
import { useRecaptcha } from './recaptcha';

type Method = 'sms' | 'totp' | null;

type Props = {
  onEnrolled?: () => void;
};

const MFAEnrollment: React.FC<Props> = ({ onEnrolled }) => {
  const { recaptchaContainerRef, recaptcha, ready, reset } = useRecaptcha();

  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // @ts-ignore
  const user = useSelector(state => state.User);

  // Re-auth screen state
  const [requiresReauth, setRequiresReauth] = useState(false);

  // UI state
  const [active, setActive] = useState<Method>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Factors state
  const [hasSms, setHasSms] = useState(false);
  const [hasTotp, setHasTotp] = useState(false);
  const [factorTotal, setFactorTotal] = useState(0);

  // Phone details
  const [enrolledPhoneUid, setEnrolledPhoneUid] = useState<string | null>(null);
  const [enrolledPhoneMasked, setEnrolledPhoneMasked] = useState<string | null>(
    null
  );

  // TOTP list (for safe removal UI)
  const [totpUids, setTotpUids] = useState<string[]>([]);

  // TOTP flow
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [totpCode, setTotpCode] = useState<string>('');
  const totpRef = useRef<HTMLInputElement>(null);

  // SMS flow
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [smsCode, setSmsCode] = useState<string>('');
  const [verificationId, setVerificationId] = useState<string>('');
  const [smsSent, setSmsSent] = useState<boolean>(false);
  const [replaceExistingPhone, setReplaceExistingPhone] = useState<boolean>(
    true
  );
  const smsCodeRef = useRef<HTMLInputElement>(null);

  const userEmail = useMemo(
    () => auth.currentUser?.email ?? 'user@example.com',
    [auth.currentUser]
  );

  // // One-time, memoized reCAPTCHA
  // const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  // //const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);

  // // create once
  // useEffect(() => {
  //   if (!recaptchaContainerRef.current || recaptchaRef.current) return;

  //   // Create with the actual element, not a string id
  //   recaptchaRef.current = new RecaptchaVerifier(
  //     auth,
  //     recaptchaContainerRef.current,
  //     {
  //       size: 'invisible'
  //     }
  //   );

  //   // Render the widget explicitly (good practice)
  //   recaptchaRef.current.render();

  //   // cleanup on unmount
  //   return () => {
  //     console.log('unmounting');
  //     try {
  //       recaptchaRef.current?.clear();
  //     } catch {}
  //     recaptchaRef.current = null;
  //   };
  // }, [auth]);

  function clearBanners() {
    setStatus('');
    setError('');
  }

  // some mfa actions require a recent login
  const loginAgain = async () => {
    signOut(auth)
      .then(() => {
        // Sign-out successful.
        if (location?.state?.customLoginPage) {
          navigate(location.state.customLoginPage);
          return;
        }

        navigate('/admin');
      })
      .catch(error => {
        console.log(error);
      });
  };

  // ----- Load enrolled factors
  const refreshFactors = async () => {
    const user = auth.currentUser as User | null;
    if (!user) return;

    await reload(user); // ensure fresh
    const mfaUser = multiFactor(user) as MultiFactorUser;
    const factors = mfaUser.enrolledFactors || [];

    const phoneFactors = factors.filter(f => f.factorId === 'phone');
    const totpFactors = factors.filter(f => f.factorId === 'totp');

    setHasSms(phoneFactors.length > 0);
    setHasTotp(totpFactors.length > 0);
    setFactorTotal(factors.length);

    // capture TOTP uids for removal UI
    setTotpUids(totpFactors.map(f => f.uid));

    // capture phone details (masked)
    const firstPhone = phoneFactors[0] as
      | (MultiFactorInfo & { phoneNumber?: string })
      | undefined;
    setEnrolledPhoneUid(firstPhone?.uid ?? null);
    setEnrolledPhoneMasked((firstPhone as any)?.phoneNumber ?? null);
  };

  useEffect(() => {
    void refreshFactors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Safe removal rule: only allow if another factor remains
  const canRemoveAnyFactor = factorTotal > 1;

  // ----- TOTP Enrollment (max 1)
  const startTOTPEnrollment = async () => {
    try {
      clearBanners();

      // Policy: only one TOTP allowed
      if (hasTotp) {
        setError(
          'You already have an authenticator set up. Remove it to add another.'
        );
        return;
      }

      setBusy(true);

      const user = auth.currentUser as User;
      const mfaUser = multiFactor(user) as MultiFactorUser;
      const session = await mfaUser.getSession();

      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(secret);

      const qrUri = secret.generateQrCodeUrl(
        userEmail,
        portalSettings.portalName
      );
      setQrCodeUrl(qrUri);

      setStatus(
        'Scan the QR code with your authenticator app, then enter the 6-digit code.'
      );
      setTimeout(() => totpRef.current?.focus(), 0);
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        setRequiresReauth(true);
        return;
      }
      console.error(err);
      setError(err?.message || 'Error starting authenticator setup.');
    } finally {
      setBusy(false);
    }
  };

  const verifyTOTPEnrollment = async (code?: string) => {
    try {
      clearBanners();
      setBusy(true);
      if (!totpSecret) throw new Error('Missing TOTP secret.');

      const user = auth.currentUser as User;
      const mfaUser = multiFactor(user) as MultiFactorUser;

      const totpCode =
        (code && code.trim && code?.trim()) ||
        totpRef.current?.value.trim() ||
        '';

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        totpCode
      );
      await mfaUser.enroll(assertion, 'Authenticator App');

      setStatus('✅ Authenticator enrolled successfully.');
      setQrCodeUrl(null);
      setTotpCode('');
      setTotpSecret(null);
      await refreshFactors();
      if (onEnrolled) onEnrolled();
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        setRequiresReauth(true);
        return;
      }
      console.error(err);
      setError(err?.message || 'Error verifying authenticator code.');
    } finally {
      setBusy(false);
    }
  };

  const removeTotpFactor = async (uid: string) => {
    try {
      clearBanners();
      setBusy(true);
      if (!canRemoveAnyFactor) {
        setError('Add SMS or another method before removing this.');
        return;
      }
      const user = auth.currentUser as User;
      const mfaUser = multiFactor(user) as MultiFactorUser;
      await mfaUser.unenroll(uid);
      setStatus('Authenticator removed.');
      await refreshFactors();
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        setRequiresReauth(true);
        return;
      }
      console.error(err);
      setError(err?.message || 'Could not remove authenticator.');
    } finally {
      setBusy(false);
    }
  };

  // ----- SMS Enrollment / Update (max 1)
  const startSMSEnrollment = async (phone?: string) => {
    try {
      clearBanners();

      if (!recaptcha || !ready) {
        // not ready; you can retry reset() or show a message
        await reset();
        return;
      }

      // Optionally force-verify to ensure a fresh token
      try {
        await recaptcha.verify();
      } catch {}

      const phoneNumber =
        (phone && phone.trim && phone?.trim()) ||
        phoneInputRef.current?.value.trim() ||
        '';

      // Validate actual phone number value (E.164 like +44..., +1..., etc.)
      if (!phoneNumber || !/^\+\d{6,}$/.test(phoneNumber)) {
        setError(
          'Please enter a valid phone number (E.164, e.g., +44… or +1…).'
        );
        return;
      }

      setBusy(true);

      const user = auth.currentUser as User | null;
      if (!user) {
        setError('You must be signed in to enroll SMS.');
        return;
      }

      // Force-refresh the ID token (picks up new claims & resets expiry)
      await user.getIdToken(true);

      // Ensure profile/factors list is fresh
      await reload(user);

      const mfaUser = multiFactor(user) as MultiFactorUser;

      // If user already has SMS and also has TOTP (total = 2), and wants to replace phone,
      // we must UNENROLL the old phone first (avoid hitting the 2-factor cap)
      if (hasSms && hasTotp && replaceExistingPhone && enrolledPhoneUid) {
        try {
          await mfaUser.unenroll(enrolledPhoneUid);
          await refreshFactors(); // keep local state in sync
        } catch (e) {
          if (
            e?.code === 'auth/requires-recent-login' ||
            e?.code === 'auth/user-token-expired'
          ) {
            setRequiresReauth(true);
            return;
          }
          console.error(e);
          setError(
            e?.message ||
              'Could not remove existing phone factor. Please try again.'
          );
          return;
        }
      }

      // If already has SMS and NOT replacing, block (max 1 SMS allowed)
      if (hasSms && !replaceExistingPhone) {
        setError(
          'You already have a phone number set up. Choose “Replace existing phone factor” to update it.'
        );
        return;
      }

      // Use the STABLE reCAPTCHA verifier created once in useEffect
      // const appVerifier = recaptchaRef.current;
      // if (!appVerifier) {
      //   setError('reCAPTCHA is not ready. Please wait a moment and try again.');
      //   return;
      // }

      // // Optional: pre-verify to ensure widget is alive (prevents "client element removed")
      // try {
      //   await appVerifier.verify();
      // } catch {
      //   // ignore; verifyPhoneNumber will trigger a challenge if needed
      // }

      const session = await mfaUser.getSession();
      const provider = new PhoneAuthProvider(auth);

      const vid = await provider.verifyPhoneNumber(
        { phoneNumber, session },
        recaptcha
      );

      setVerificationId(vid);
      setSmsSent(true);
      setStatus('SMS sent. Enter the code below.');
      setTimeout(() => smsCodeRef.current?.focus(), 0);
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        setRequiresReauth(true);
        return;
      }
      if (err?.code === 'auth/captcha-check-failed') {
        setError('reCAPTCHA check failed. Please try again.');
        return;
      }
      if (err?.message?.includes('reCAPTCHA client element has been removed')) {
        setError(
          'Security check expired. Please stay on this page and try again.'
        );
        return;
      }
      console.error(err);
      setError(err?.message || 'Error starting SMS setup.');
    } finally {
      setBusy(false);
    }
  };

  const verifySMSEnrollment = async (code?: string) => {
    try {
      clearBanners();
      setBusy(true);
      if (!verificationId) throw new Error('Missing verification ID.');

      const smsCode =
        (code && code.trim && code?.trim()) ||
        smsCodeRef.current?.value.trim() ||
        '';
      const cred = PhoneAuthProvider.credential(verificationId, smsCode);
      const user = auth.currentUser as User;
      const mfaUser = multiFactor(user) as MultiFactorUser;

      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await mfaUser.enroll(assertion, 'Phone Number');

      setStatus('✅ SMS factor enrolled successfully.');
      setSmsCode('');
      setVerificationId('');
      setSmsSent(false);
      await refreshFactors();
      if (onEnrolled) onEnrolled();
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        setRequiresReauth(true);
        return;
      }
      console.error(err);
      setError(err?.message || 'Error verifying SMS code.');
    } finally {
      setBusy(false);
    }
  };

  // ----- Icons (inline)
  const CheckIcon = () => (
    <svg
      className="h-5 w-5 text-emerald-600"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-5.121-5.121a1 1 0 111.414-1.414L8.414 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
  const PhoneIcon = () => (
    <svg
      className="h-8 w-8 text-indigo-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h1.5a1.5 1.5 0 001.5-1.5v-2.25a1.5 1.5 0 00-1.5-1.5h-2.25a1.5 1.5 0 00-1.5 1.5v.09a11.25 11.25 0 01-9.84-9.84h.09a1.5 1.5 0 001.5-1.5V3.75A1.5 1.5 0 006.75 2.25H4.5A1.5 1.5 0 003 3.75v3z"
      />
    </svg>
  );
  const ShieldIcon = () => (
    <svg
      className="h-8 w-8 text-emerald-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M12 3l7.5 3v5.25c0 4.28-2.99 8.1-7.5 9-4.51-.9-7.5-4.72-7.5-9V6L12 3z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M9.75 12.75l1.5 1.5 3-3"
      />
    </svg>
  );

  const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
      {children}
    </div>
  );

  const TileButton: React.FC<{
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    testId?: string;
    checked?: boolean; // green tick if already enrolled
    onClick: () => void;
  }> = ({ title, subtitle, icon, testId, checked, onClick }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left p-4 border rounded-md transition shadow-sm border-gray-200 hover:border-indigo-300 hover:shadow">
      <div className="flex items-start gap-4">
        <div className="shrink-0">{icon}</div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-gray-900">{title}</div>
            {checked ? <CheckIcon /> : null}
          </div>
          <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
        </div>
      </div>
    </button>
  );

  return (
    <div
      className="min-h-full flex flex-col py-12 sm:px-6 lg:px-8"
      data-testid="mfa-enrollment">
      {requiresReauth ? (
        <div
          className="min-h-full flex flex-col py-12 sm:px-6 lg:px-8 bg-gray-50"
          data-testid="reauth-required">
          <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
            <img
              className="mx-auto h-12 w-auto mb-6"
              src={CompanyLogo}
              alt="Company Logo"
            />
            <h2 className="mt-8 text-3xl font-extrabold text-gray-900">
              Please re-login
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              For your security, we need you to sign in again before continuing
              your multi-factor setup.
            </p>

            <div className="mt-6 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <button
                onClick={loginAgain}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                Return to Login
              </button>
            </div>

            <p className="mt-6 text-xs text-gray-400">
              You’ll be redirected back here after signing in again.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img
              className="mx-auto h-12 w-auto mb-6"
              src={CompanyLogo}
              alt="Company Logo"
            />
            <h2 className="mt-8 text-center text-3xl font-extrabold text-gray-900">
              Set up two-step verification
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {user?.validUser ? (
                <span>
                  Configure Multi-Factor Authentication for your account
                </span>
              ) : (
                <span>
                  For your security, you’re required to set up at least one
                  multi-factor method before continuing
                </span>
              )}
            </p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <Card>
              {/* banners */}
              {error && (
                <div className="text-red-700 mb-3" data-testid="mfaError">
                  {error}
                </div>
              )}
              {status && (
                <div className="text-green-700 mb-3" data-testid="mfaStatus">
                  {status}
                </div>
              )}

              {/* Back link when a method is active */}
              {active && (
                <button
                  type="button"
                  onClick={() => {
                    clearBanners();
                    setActive(null);
                    setQrCodeUrl(null);
                    setTotpSecret(null);
                    setTotpCode('');
                    setSmsCode('');
                    setSmsSent(false);
                  }}
                  className="mb-4 text-sm text-gray-500 hover:text-gray-700 inline-flex items-center">
                  <span className="mr-2">←</span> Back to options
                </button>
              )}

              {/* Selection tiles (hidden when a method is active) */}
              {!active && (
                <div className="grid gap-4 mb-6">
                  <TileButton
                    title="Setup SMS"
                    subtitle={
                      hasSms
                        ? enrolledPhoneMasked
                          ? `Already set up (${enrolledPhoneMasked}). Click to update.`
                          : 'Already set up. Click to update.'
                        : 'Use your mobile phone to receive a text code.'
                    }
                    icon={<PhoneIcon />}
                    checked={hasSms}
                    onClick={() => {
                      clearBanners();
                      setActive('sms');
                    }}
                    testId="tileSms"
                  />
                  <TileButton
                    title="Setup Authenticator"
                    subtitle={
                      hasTotp
                        ? 'Already set up.'
                        : 'Use an authenticator app (TOTP) like Google Authenticator or Authy.'
                    }
                    icon={<ShieldIcon />}
                    checked={hasTotp}
                    onClick={() => {
                      clearBanners();
                      setActive('totp');
                    }}
                    testId="tileTotp"
                  />
                </div>
              )}

              {/* SMS block */}
              <div
                className={active === 'sms' ? 'block' : 'hidden'}
                aria-hidden={active !== 'sms'}>
                <div className="">
                  {hasSms && (
                    <div className="mb-3 text-sm text-gray-700">
                      Current number:{' '}
                      <span className="font-medium">
                        {enrolledPhoneMasked ?? '—'}
                      </span>
                    </div>
                  )}

                  <label className="block text-sm font-medium text-gray-700">
                    Phone number
                  </label>
                  <div className="mt-2 flex gap-2">
                    <input
                      ref={phoneInputRef}
                      id="txtSmsPhoneInput"
                      data-testid="smsPhoneInput"
                      type="tel"
                      placeholder="e.g. +44xxxxxxxxxx"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          startSMSEnrollment(
                            (e.currentTarget as HTMLInputElement).value
                          );
                        }
                      }}
                      className="px-3 py-2 flex-1 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <div>
                      <button
                        data-testid="smsSendButton"
                        onClick={() => startSMSEnrollment()}
                        disabled={busy}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60">
                        {busy
                          ? 'Sending…'
                          : hasSms
                          ? 'Update Number'
                          : 'Send SMS'}
                      </button>
                    </div>
                  </div>

                  {hasSms && (
                    <label className="mt-3 inline-flex items-center text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replaceExistingPhone}
                        onChange={e =>
                          setReplaceExistingPhone(e.target.checked)
                        }
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded mr-2"
                      />
                      Replace existing phone factor
                    </label>
                  )}

                  {/* Keep subtree mounted; toggle visibility to avoid focus loss */}
                  <div
                    className={
                      smsSent
                        ? 'opacity-100 mt-4'
                        : 'opacity-0 pointer-events-none h-0 overflow-hidden'
                    }>
                    <label className="block text-sm font-medium text-gray-700">
                      Verification code
                    </label>
                    <div className="mt-1 flex gap-2">
                      <input
                        ref={smsCodeRef}
                        data-testid="smsCodeInput"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="123456"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            verifySMSEnrollment(
                              (e.currentTarget as HTMLInputElement).value
                            );
                          }
                        }}
                        className="appearance-none block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                      <button
                        data-testid="smsVerifyButton"
                        onClick={() => verifySMSEnrollment()}
                        disabled={busy}
                        className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60">
                        {busy ? 'Verifying…' : 'Verify'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* TOTP block */}
              <div
                className={active === 'totp' ? 'block' : 'hidden'}
                aria-hidden={active !== 'totp'}>
                <div className="border rounded-md p-4 space-y-4">
                  {/* If TOTP already set, show summary + removal (no add-another) */}
                  {hasTotp && !qrCodeUrl && (
                    <div className="text-sm text-gray-700">
                      You already have an authenticator set up.
                      {totpUids.length > 0 && (
                        <div className="mt-4">
                          <div className="text-xs text-gray-500 mb-1">
                            Enrolled authenticator
                          </div>
                          <ul className="space-y-2">
                            {totpUids.map(uid => {
                              const canRemove = canRemoveAnyFactor; // at least one other factor remains
                              return (
                                <li
                                  key={uid}
                                  className="flex items-center justify-between border rounded px-3 py-2">
                                  <span className="text-sm text-gray-800">
                                    Authenticator • {uid.slice(0, 6)}…
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      canRemove && removeTotpFactor(uid)
                                    }
                                    disabled={!canRemove || busy}
                                    className={[
                                      'text-sm',
                                      canRemove
                                        ? 'text-red-700 hover:text-red-800'
                                        : 'text-gray-400 cursor-not-allowed'
                                    ].join(' ')}
                                    title={
                                      canRemove
                                        ? 'Remove this authenticator'
                                        : 'Add SMS first before removing this'
                                    }>
                                    Remove
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                          {factorTotal <= 1 && (
                            <p className="mt-2 text-xs text-amber-700">
                              You currently have only one sign-in method. Add
                              SMS before removing the authenticator.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fresh enrollment flow when no TOTP */}
                  {!hasTotp && (
                    <>
                      {!qrCodeUrl ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-700">
                            You’ll scan a QR code with your authenticator app,
                            then enter the 6-digit code it shows.
                          </p>
                          <button
                            data-testid="totpStartButton"
                            onClick={startTOTPEnrollment}
                            disabled={busy}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60">
                            {busy ? 'Preparing…' : 'Generate QR Code'}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex flex-col items-center">
                            <QRCode value={qrCodeUrl} size={180} />
                            <div className="text-xs text-gray-500 mt-2">
                              Can’t scan? Use secret:{' '}
                              <span className="font-mono select-all">
                                {totpSecret?.secretKey}
                              </span>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              6-digit code
                            </label>
                            <div className="mt-1 flex gap-2">
                              <input
                                ref={totpRef}
                                data-testid="totpCodeInput"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="123456"
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    verifyTOTPEnrollment(
                                      (e.currentTarget as HTMLInputElement)
                                        .value
                                    );
                                  }
                                }}
                                className="appearance-none block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <button
                                data-testid="totpVerifyButton"
                                onClick={() => verifyTOTPEnrollment()}
                                disabled={busy || !totpCode}
                                className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60">
                                {busy ? 'Verifying…' : 'Verify'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* subtle footer like login page */}
              <div className="mt-6 text-center text-xs text-gray-400">
                {user?.validUser ? (
                  <a href="#" onClick={() => navigate('/')}>
                    Home
                  </a>
                ) : null}
              </div>
            </Card>
          </div>
        </>
      )}

      {/* reCAPTCHA anchor — keep mounted */}
      <div
        ref={recaptchaContainerRef}
        id="recaptcha-container"
        className="hidden sr-only"
      />
    </div>
  );
};

export default MFAEnrollment;
